import { storeToRefs } from 'pinia'
import {
  deviceManagementPrintService,
  useDeviceManagementStore
} from '@nsftx/seven-client-retail-vue-dm'
import { logService } from '@nsftx/seven-client-retail-utils'
import {
  printService,
  DEFAULT_PRINT_CURRENCY,
  DEFAULT_PRINT_NUMBER_FORMAT,
  type PrintContext,
  type PrintOptions,
  type PrintGlobalsBasic
} from '@nsftx/seven-client-retail-print'
import { cmsService } from '@nsftx/seven-client-retail-vue-sgp'
import { useCmsConfigStore } from '@/stores/cms/cmsConfigStore'

const LOG_PREFIX = '[printService]'

let printServiceInstance: null | ReturnType<typeof printService.createPrint>

const initPrintService = () => {
  const { cmsConfig } = storeToRefs(useCmsConfigStore())
  const { appData } = storeToRefs(useDeviceManagementStore())
  const config = cmsConfig.value
  const checkStatusBeforePrintConfig = config?.appSettings.checkPrinterStatusBefore || false
  const checkStatusAfterPrintConfig = config?.appSettings.checkPrinterStatusAfter || false

  const globals: PrintGlobalsBasic = {
    location: {
      name: appData.value?.betshop.name || 'Unknown Betshop'
    },
    device: {
      name: appData.value?.device.name || 'Unknown Device'
    }
  }

  const spaces: PrintOptions['spaces'] = Object.fromEntries(
    Object.entries(cmsService.createSpacesFromTemplates(config?.templates.docs ?? [])).map(
      ([spaceName, space]) => [
        spaceName,
        {
          compilerOptions: {
            engineConfig: {
              templates: space.templates,
              globals
            }
          }
        }
      ]
    )
  )

  const options: PrintOptions = {
    driver: {
      getPrinterStatus: deviceManagementPrintService.getPrinterStatus,
      addJob: <T>(template: string, options: PrintContext<T>) => {
        logService.debug(`${LOG_PREFIX} Passing print job to DM API`, {
          code: 'SBO_PRINT_ADD_PRINT_JOB'
        })
        return deviceManagementPrintService.addPrintJob(template, options)
      }
    },
    spaces,
    checkStatusBeforePrint: checkStatusBeforePrintConfig,
    checkStatusAfterPrint: checkStatusAfterPrintConfig,
    numberFormat: DEFAULT_PRINT_NUMBER_FORMAT,
    currency: DEFAULT_PRINT_CURRENCY
  }

  printServiceInstance = printService.createPrint(options)
}

const getPrintService = () => {
  if (!printServiceInstance) {
    throw new Error('Print service not initialized. Please call initPrintService first.')
  }

  return printServiceInstance
}

const addOperatorDataToGlobals = (operatorName: string) => {
  if (!printServiceInstance) {
    throw new Error('Print service not initialized. Please call initPrintService first.')
  }

  printServiceInstance.updateGlobals('sbo', {
    operator: {
      name: operatorName
    }
  })
}

export default {
  initPrintService,
  getPrintService,
  addOperatorDataToGlobals
}
